/**
 * One requisition can be issued as several supplier purchase orders.
 * Each PO is purchased on its own. The requisition stays issued until every
 * supplier PO has a record in `orderPurchases`.
 */
import type { PurchaseLineItem, PurchaseRequisition } from '../types/purchasing';
import { issuedVendorGroups, purchaseOrderNumbers } from './purchaseLineVendors';
import { requisitionTotal, roundMoney } from './purchasingLineItems';

export interface PurchasedSupplierOrder {
  poNumber: string;
  vendorName?: string;
  purchasedAt: Date;
  purchasedById: string;
  purchasedByName: string;
  purchasedAmount: number;
}

export interface IssuedSupplierPurchaseOrder {
  poNumber: string;
  vendorName: string;
  amount: number;
}

type SupplierPurchaseRow = {
  poNumber?: string | null;
  vendorName?: string | null;
  amount?: number;
  purchasedAmount?: number | null;
  salesTaxPct?: number;
  status?: string;
  lineItems?: PurchaseLineItem[];
  orderPurchases?: PurchasedSupplierOrder[];
};

function asDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    const date = (value as { toDate: () => Date }).toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : undefined;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }
  return undefined;
}

export function parsePurchasedSupplierOrders(raw: unknown): PurchasedSupplierOrder[] | undefined {
  if (!Array.isArray(raw) || !raw.length) return undefined;
  const out: PurchasedSupplierOrder[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const poNumber = typeof row.poNumber === 'string' ? row.poNumber.trim() : '';
    const purchasedAmount = Number(row.purchasedAmount);
    if (!poNumber || !Number.isFinite(purchasedAmount) || purchasedAmount < 0) continue;
    out.push({
      poNumber,
      ...(typeof row.vendorName === 'string' && row.vendorName.trim()
        ? { vendorName: row.vendorName.trim() }
        : {}),
      purchasedAt: asDate(row.purchasedAt) || new Date(),
      purchasedById: typeof row.purchasedById === 'string' ? row.purchasedById : '',
      purchasedByName: typeof row.purchasedByName === 'string' ? row.purchasedByName : '',
      purchasedAmount: roundMoney(purchasedAmount),
    });
  }
  return out.length ? out : undefined;
}

export function issuedSupplierPurchaseOrders(row: SupplierPurchaseRow): IssuedSupplierPurchaseOrder[] {
  const groups = issuedVendorGroups(row.lineItems || [], row.vendorName, row.poNumber).filter(
    group => group.poNumber,
  );
  if (groups.length <= 1) return [];
  const tax = Number(row.salesTaxPct) || 0;
  return groups.map(group => ({
    poNumber: group.poNumber,
    vendorName: group.vendorName,
    amount: requisitionTotal(group.lines, tax),
  }));
}

export function isMultiSupplierIssuedPurchase(row: SupplierPurchaseRow): boolean {
  return issuedSupplierPurchaseOrders(row).length > 1;
}

export function supplierPurchaseOrderByNumber(
  row: SupplierPurchaseRow,
  poNumber: string,
): IssuedSupplierPurchaseOrder | undefined {
  const target = poNumber.trim();
  return issuedSupplierPurchaseOrders(row).find(order => order.poNumber === target);
}

export function purchasedSupplierOrders(row: SupplierPurchaseRow): PurchasedSupplierOrder[] {
  return row.orderPurchases || [];
}

export function findPurchasedSupplierOrder(
  row: SupplierPurchaseRow,
  poNumber: string,
): PurchasedSupplierOrder | undefined {
  const target = poNumber.trim();
  return purchasedSupplierOrders(row).find(order => order.poNumber === target);
}

/** PO numbers already purchased that the next issue would drop. */
export function droppedPurchasedPoNumbers(
  orderPurchases: Array<{ poNumber: string }> | undefined,
  nextPoNumbers: string[],
): string[] {
  const next = new Set(nextPoNumbers.map(number => number.trim()).filter(Boolean));
  const dropped: string[] = [];
  for (const order of orderPurchases || []) {
    const poNumber = order.poNumber.trim();
    if (poNumber && !next.has(poNumber) && !dropped.includes(poNumber)) dropped.push(poNumber);
  }
  return dropped;
}

/**
 * Open vs purchased dollars on one requisition.
 * A single PO uses the document amount. Several supplier POs split by group,
 * and a purchased group's receipt total is the purchased side.
 */
export function splitSupplierPurchaseCommitment(row: SupplierPurchaseRow): {
  open: number;
  purchased: number;
} {
  const amount = Number.isFinite(Number(row.amount)) ? Number(row.amount) : 0;
  const receipt =
    typeof row.purchasedAmount === 'number' && Number.isFinite(row.purchasedAmount)
      ? row.purchasedAmount
      : null;
  if (!isMultiSupplierIssuedPurchase(row)) {
    if (row.status === 'purchased') {
      return { open: 0, purchased: Math.max(0, receipt ?? amount) };
    }
    return { open: Math.max(0, amount), purchased: 0 };
  }
  if (row.status === 'purchased') {
    return { open: 0, purchased: Math.max(0, receipt ?? amount) };
  }
  const bought = new Set(purchasedSupplierOrders(row).map(order => order.poNumber));
  let open = 0;
  for (const order of issuedSupplierPurchaseOrders(row)) {
    if (!bought.has(order.poNumber)) open += order.amount;
  }
  let purchased = 0;
  for (const order of purchasedSupplierOrders(row)) {
    if (issuedSupplierPurchaseOrders(row).some(group => group.poNumber === order.poNumber)) {
      purchased += order.purchasedAmount;
    }
  }
  return {
    open: roundMoney(Math.max(0, open)),
    purchased: roundMoney(Math.max(0, purchased)),
  };
}

export type CardExpenseLink = {
  id?: string;
  purchaseRequisitionId?: string | null;
  poNumber?: string | null;
};

/** A card charge linked to this PO number, or to the requisition with no PO number. */
export function cardExpenseCoversSupplierPo(
  row: {
    id?: string;
    qbExpenseIds?: string[];
    qbExpenseId?: string | null;
  },
  poNumber: string,
  expenses: CardExpenseLink[],
): boolean {
  const target = poNumber.trim();
  if (!target || !expenses.length) return false;
  const linkedIds = new Set<string>();
  for (const id of row.qbExpenseIds || []) {
    const trimmed = (id || '').trim();
    if (trimmed) linkedIds.add(trimmed);
  }
  const single = (row.qbExpenseId || '').trim();
  if (single) linkedIds.add(single);
  const requisitionId = (row.id || '').trim();
  return expenses.some(expense => {
    const linkedPo = String(expense.poNumber || '').trim();
    if (linkedPo && linkedPo === target) return true;
    if (linkedPo) return false;
    const expenseId = (expense.id || '').trim();
    if (expenseId && linkedIds.has(expenseId)) return true;
    const linkedPr = (expense.purchaseRequisitionId || '').trim();
    return Boolean(requisitionId && linkedPr && linkedPr === requisitionId);
  });
}

export function everySupplierPoPurchased(row: SupplierPurchaseRow): boolean {
  const orders = issuedSupplierPurchaseOrders(row);
  if (orders.length <= 1) return false;
  return orders.every(order => findPurchasedSupplierOrder(row, order.poNumber));
}

/** Which PO an agent mark-purchased call is allowed to close. */
export function selectPurchaseOrderToMark(
  poNumbers: string[],
  requested?: string | null,
): { ok: true; poNumber: string } | { ok: false; code: string; error: string } {
  const numbers = purchaseOrderNumbers({
    lineItems: poNumbers.map(poNumber => ({ poNumber })),
  });
  const target = String(requested || '').trim();
  if (numbers.length > 1) {
    if (!target || !numbers.includes(target)) {
      return {
        ok: false,
        code: 'po_number_required',
        error: `This requisition has several purchase orders. Pass poNumber: ${numbers.join(', ')}.`,
      };
    }
    return { ok: true, poNumber: target };
  }
  const only = target || numbers[0] || '';
  if (!only) {
    return {
      ok: false,
      code: 'missing_po_number',
      error: 'PO number missing — issue the PO in the CRM before marking purchased.',
    };
  }
  if (target && numbers.length === 1 && target !== numbers[0]) {
    return {
      ok: false,
      code: 'unknown_po_number',
      error: `${target} is not a purchase order on this requisition.`,
    };
  }
  return { ok: true, poNumber: only };
}

export function sumPurchasedSupplierOrders(orders: Array<{ purchasedAmount: number }>): number {
  return roundMoney(orders.reduce((sum, order) => sum + (Number(order.purchasedAmount) || 0), 0));
}

/** Document amount still open on an approved multi-supplier requisition. */
export function openSupplierPurchaseAmount(row: PurchaseRequisition | SupplierPurchaseRow): number {
  return splitSupplierPurchaseCommitment(row).open;
}
